import { Button } from '@hqo/ui-atoms';
import { ComponentProps, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

import { cn } from '@hqo/react-hooks';

type ButtonComponentProps = ComponentProps<typeof Button>;

interface LoadingButtonProps {
  type?: 'button' | 'submit' | 'reset';
  variant?: ButtonComponentProps['variant'];
  size?: ButtonComponentProps['size'];
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  onClick?: ButtonComponentProps['onClick'];
  children: ReactNode;
  spinnerClassName?: string;
  'data-testid'?: string;
  'aria-label'?: string;
}

export const LoadingButton = ({
  type = 'button',
  variant,
  size,
  className,
  disabled,
  loading,
  onClick,
  children,
  spinnerClassName,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: LoadingButtonProps): JSX.Element => (
  <Button
    type={type}
    variant={variant}
    size={size}
    className={cn('relative', className)}
    disabled={disabled || loading}
    onClick={onClick}
    data-testid={dataTestId}
    aria-label={ariaLabel}
  >
    <span className={cn(loading && 'invisible')}>{children}</span>
    {loading && (
      // Spinner is wrapped in a `<span>` so the Button's `has-[>svg]:px-*`
      // selector doesn't match (Button shrinks its horizontal padding when
      // a direct-child SVG appears). Keeping the SVG nested preserves the
      // button width 1:1 across the (loaded ↔ loading) transition.
      <span
        aria-hidden="true"
        className="absolute inset-0 flex items-center justify-center"
      >
        <Loader2
          className={cn('size-5 animate-spin text-current', spinnerClassName)}
        />
      </span>
    )}
  </Button>
);
